import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RelatedListSidebar, NotesPanel, type RelatedTab } from "@/components/related-list-sidebar";
import { ActivitiesPanel } from "@/components/activities-panel";
import { EmailComposer } from "@/components/email-composer";
import { AttachmentsPanel } from "@/components/attachments-panel";
import { QuickAddTask } from "@/components/quick-add-task";
import { QuickAddMeeting } from "@/components/quick-add-meeting";
import { QuickLogActivity } from "@/components/quick-log-activity";
import { DealContactsPanel } from "@/components/deal-contacts-panel";
import { DealProductsPanel } from "@/components/deal-products-panel";
import { CompetitorsPanel } from "@/components/competitors-panel";
import { Mail, Trash2 } from "lucide-react";

function KV({ k, v }: { k: string; v: React.ReactNode }) {
  if (v === null || v === undefined || v === "" || v === false) return null;
  return (
    <div className="flex justify-between gap-4 border-b py-1.5 last:border-0">
      <span className="text-xs text-muted-foreground">{k}</span>
      <span className="text-sm text-right">{v}</span>
    </div>
  );
}

type DealTab = "contactRoles" | "products" | "competitors" | RelatedTab;
const RELATED_TABS: RelatedTab[] = ["notes", "activities", "meetings", "tasks", "attachments", "emails"];

export default function DealDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState<DealTab>("contactRoles");
  const [composerOpen, setComposerOpen] = useState(false);

  const queryKey = ["/deals", id];
  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => (await api.get(`/deals/${id}`)).data,
    enabled: !!id,
  });

  async function del() {
    if (!data || !confirm(`Delete deal "${data.name}"?`)) return;
    await api.delete(`/deals/${id}`);
    qc.invalidateQueries({ queryKey: ["/deals"] });
    navigate("/deals");
  }

  if (isLoading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  if (!data) return <div className="p-6 text-sm text-muted-foreground">Not found.</div>;

  const expectedRevenue = (Number(data.valueUsd) || 0) * (Number(data.probability) / 100);
  const stageWon   = data.stage === "CLOSED_WON" || data.stage === "AWARDED" || data.stage === "PLACED";
  const stageLost  = data.stage === "CLOSED_LOST" || data.stage === "LOST";

  return (
    <>
      <PageHeader
        title={data.name}
        description={`${data.pipelineType} · ${data.stage.replace(/_/g, " ")}`}
        actions={
          <>
            <Button variant="outline" onClick={() => setComposerOpen(true)}><Mail className="mr-1 h-4 w-4"/>Send Email</Button>
            <Button variant="outline" onClick={del}><Trash2 className="mr-1 h-4 w-4"/>Delete</Button>
          </>
        }
      />

      <div className="flex flex-1 min-h-[70vh]">
        {/* Custom sidebar — Contact Roles + Products first, then generic related lists */}
        <aside className="w-52 shrink-0 border-r bg-card">
          <div className="border-b px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Related</div>
          <ul>
            <SideTab label="Contact Roles" active={tab === "contactRoles"} onClick={() => setTab("contactRoles")} />
            <SideTab label="Products"      active={tab === "products"}      onClick={() => setTab("products")} />
            <SideTab label="Competitors"   active={tab === "competitors"}   onClick={() => setTab("competitors")} />
            {RELATED_TABS.map((t) => (
              <SideTab key={t} label={t.charAt(0).toUpperCase() + t.slice(1)} active={tab === t} onClick={() => setTab(t)} />
            ))}
          </ul>
        </aside>

        <div className="flex-1 overflow-auto p-6 space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Amount</CardTitle></CardHeader>
              <CardContent className="text-2xl font-semibold tabular-nums">
                ${Number(data.valueUsd).toLocaleString()}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Expected Revenue</CardTitle></CardHeader>
              <CardContent className="text-2xl font-semibold tabular-nums">
                ${Math.round(expectedRevenue).toLocaleString()}
                <span className="ml-2 text-xs font-normal text-muted-foreground">{data.probability}%</span>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Close Date</CardTitle></CardHeader>
              <CardContent className="text-lg">
                {data.closeDate ? new Date(data.closeDate).toLocaleDateString() : "—"}
                {stageWon && <Badge className="ml-2" variant="default">Won</Badge>}
                {stageLost && <Badge className="ml-2" variant="destructive">Lost</Badge>}
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader><CardTitle className="text-base">Deal information</CardTitle></CardHeader>
              <CardContent>
                <KV k="Pipeline" v={<Badge variant="secondary">{data.pipelineType}</Badge>} />
                <KV k="Stage" v={<Badge variant="secondary">{data.stage.replace(/_/g, " ")}</Badge>} />
                <KV k="Type" v={data.type} />
                <KV k="Next step" v={data.nextStep} />
                <KV k="Lead source" v={data.leadSource} />
                <KV k="Entity" v={data.entity} />
                <KV k="Lost reason" v={data.lostReason} />
                <KV k="Owner" v={data.owner?.name} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Relationships</CardTitle></CardHeader>
              <CardContent>
                <KV k="Account" v={data.account ? <Link className="hover:underline" to={`/accounts/${data.account.id}`}>{data.account.name}</Link> : null} />
                <KV k="Primary contact" v={data.contact ? <Link className="hover:underline" to={`/contacts/${data.contact.id}`}>{data.contact.name}</Link> : null} />
                <KV k="Contact email" v={data.contact?.email ? <a className="hover:underline" href={`mailto:${data.contact.email}`}>{data.contact.email}</a> : null} />
              </CardContent>
            </Card>

            {data.description && (
              <Card className="md:col-span-2">
                <CardHeader><CardTitle className="text-base">Description</CardTitle></CardHeader>
                <CardContent><p className="whitespace-pre-wrap text-sm">{data.description}</p></CardContent>
              </Card>
            )}

            {data.stageHistory?.length > 0 && (
              <Card className="md:col-span-2">
                <CardHeader><CardTitle className="text-base">Stage history</CardTitle></CardHeader>
                <CardContent>
                  <ul className="space-y-1 text-sm">
                    {data.stageHistory.map((h: any) => (
                      <li key={h.id} className="flex items-center justify-between border-b py-1 last:border-0">
                        <span>
                          {h.fromStage ? `${h.fromStage.replace(/_/g, " ")} → ` : ""}
                          <span className="font-medium">{h.toStage.replace(/_/g, " ")}</span>
                        </span>
                        <span className="text-xs text-muted-foreground">{new Date(h.changedAt).toLocaleString()}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>

          <Card>
            <CardHeader><CardTitle className="text-base">{tabLabel(tab)}</CardTitle></CardHeader>
            <CardContent>
              {tab === "contactRoles" && <DealContactsPanel dealId={data.id} />}
              {tab === "products"     && <DealProductsPanel dealId={data.id} dealQueryKey={queryKey} />}
              {tab === "competitors"  && <CompetitorsPanel endpoint={`/deals/${data.id}/competitors`} scope="Deal" />}
              {tab === "notes" && <NotesPanel entityType="Deal" entityId={data.id} />}
              {tab === "activities" && (
                <div className="space-y-3">
                  <QuickLogActivity
                    dealId={data.id}
                    contactId={data.contact?.id}
                    accountId={data.account?.id}
                    invalidateQueryKeys={[["/activities", { field: "dealId", id: data.id }]]}
                  />
                  <ActivitiesPanel field="dealId" id={data.id} />
                </div>
              )}
              {tab === "meetings" && (
                <div className="space-y-3">
                  <QuickAddMeeting
                    dealId={data.id}
                    contactId={data.contact?.id}
                    accountId={data.account?.id}
                    invalidateQueryKeys={[["/activities", { field: "dealId", id: data.id, type: "MEETING" }]]}
                  />
                  <ActivitiesPanel field="dealId" id={data.id} type="MEETING" emptyMessage="No meetings linked to this deal." />
                </div>
              )}
              {tab === "tasks" && (
                <div className="space-y-3">
                  <QuickAddTask
                    dealId={data.id}
                    contactId={data.contact?.id}
                    accountId={data.account?.id}
                    invalidateQueryKeys={[["/activities", { field: "dealId", id: data.id, type: "TASK" }]]}
                  />
                  <ActivitiesPanel field="dealId" id={data.id} type="TASK" emptyMessage="No tasks linked to this deal." />
                </div>
              )}
              {tab === "emails" && (
                <div className="space-y-3">
                  <QuickLogActivity
                    dealId={data.id}
                    contactId={data.contact?.id}
                    accountId={data.account?.id}
                    kinds={["EMAIL"]}
                    submitLabel="Log email"
                    invalidateQueryKeys={[["/activities", { field: "dealId", id: data.id, type: "EMAIL" }]]}
                  />
                  <ActivitiesPanel field="dealId" id={data.id} type="EMAIL" emptyMessage="No email history for this deal yet." />
                </div>
              )}
              {tab === "attachments" && <AttachmentsPanel entityType="Deal" entityId={data.id} />}
            </CardContent>
          </Card>
        </div>
      </div>

      <EmailComposer
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        contact={data.contact}
        account={data.account}
        deal={data}
        invalidateQueryKeys={[["/activities", { field: "dealId", id: data.id }]]}
      />
    </>
  );
}

function SideTab({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <li>
      <button
        onClick={onClick}
        className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm ${
          active ? "bg-primary/10 text-primary font-medium" : "text-foreground/80 hover:bg-muted"
        }`}
      >
        {label}
      </button>
    </li>
  );
}

function tabLabel(t: DealTab): string {
  switch (t) {
    case "contactRoles": return "Contact Roles";
    case "products":     return "Products / Line Items";
    case "competitors":  return "Competitors";
    default: return t.charAt(0).toUpperCase() + t.slice(1);
  }
}
